import * as mobx from 'mobx';
import * as IoC from 'typescript-ioc';
import { BaseAccountAdminSettingViewModel } from '../common/base-account-admin-setting-form.viewmodel';
import { AccountAdminSettingEntity } from '../common/account-admin-setting.entity';
import { MessageDescriptor } from 'react-intl';
import { OE_PASSPORT_ATTENDEE_SSO_DATA_UPDATED_TEXT } from '../account-admin.text';
import { FusionIdentityProviderResponseDAO, IdentityProviderResponseDAO, IPassportAttendeeSSODAO } from '../../../api/passport-attendee-sso.dao';
import { handleApiException } from '../../global/modal/alerts-host/alert-templates';
import { MANAGED_DOMAINS_TOO_LONG_ERROR_TEXT, PROVIDER_VALIDATION_TEXT } from './oe-passport-attendee-sso.text';
import { domainListValidator } from '../../../modules/validators';
import { LONG_PLAIN_TEXT_LENGTH } from '../../../constants/common.constants';

interface UnifiedProvider {
    fusionAuthIdpId: string;
    fusionAuthIdpName: string;
    enabled: boolean;
    enabledForAllEvents: boolean;
    managedDomains: string[];
    isExisting: boolean;
    accountIdpId?: number;
}

class PassportAttendeeSSOFormVM extends BaseAccountAdminSettingViewModel {
    @mobx.observable
    public isLoading: boolean = false;
    @mobx.observable
    public accountIdpProviders: IdentityProviderResponseDAO[] = [];
    @mobx.observable
    public fusionProviders: FusionIdentityProviderResponseDAO[] = [];
    @mobx.observable
    public allProviders: UnifiedProvider[] = [];
    @mobx.observable
    public domainValidationErrors: Record<string, string> = {};
    @mobx.observable
    public tempInputValue: Record<string, string> = {};
    @mobx.observable
    public formSubmitted: boolean = false;

    constructor(
        protected readonly entity: AccountAdminSettingEntity,
        @IoC.Inject private readonly passportAttendeeSSODAO: IPassportAttendeeSSODAO = IoC.Container.get(IPassportAttendeeSSODAO),
    ) {
        super(entity);
        this.initialize();
    }

    @mobx.action
    public async initialize(): Promise<void> {
        try {
            mobx.runInAction(() => {
                this.isLoading = true;
            });
            await this.fetchAccountProviders();
            await this.fetchFusionProviders();
        } catch (error) {
            handleApiException(error);
        } finally {
            mobx.runInAction(() => {
                this.isLoading = false;
            });
        }
    }

    @mobx.action
    private combineAndSortProviders(): void {
        const existingIds = new Set(this.accountIdpProviders.map(p => p.fusionAuthIdpId));
        const existingUnified: UnifiedProvider[] = this.accountIdpProviders.map(p => ({
            fusionAuthIdpId: p.fusionAuthIdpId,
            fusionAuthIdpName: p.fusionAuthIdpName,
            enabled: p.enabled,
            enabledForAllEvents: p.enabledForAllEvents ?? false,
            managedDomains: p.managedDomains ?? [],
            isExisting: true,
            accountIdpId: p.id,
        }));
        const newUnified: UnifiedProvider[] = (this.fusionProviders ?? [])
            .filter(p => !existingIds.has(p.id))
            .map(p => ({
                fusionAuthIdpId: p.id,
                fusionAuthIdpName: p.name,
                enabled: false,
                enabledForAllEvents: false,
                managedDomains: p.domains ?? [],
                isExisting: false,
            }));
        this.allProviders = [...existingUnified, ...newUnified].sort((a, b) =>
            a.fusionAuthIdpName.localeCompare(b.fusionAuthIdpName)
        );
    }

    @mobx.action
    public async fetchFusionProviders(): Promise<void> {
        try {
            const providersResponse = await this.passportAttendeeSSODAO.getFusionPassportIdentityProviders();
            mobx.runInAction(() => {
                this.fusionProviders = providersResponse;
                this.combineAndSortProviders();
            });
        } catch (error) {
            handleApiException(error);
        }
    }

    @mobx.action
    public async fetchAccountProviders(): Promise<void> {
        try {
            const params = { accountId: this.entity.accountId };
            const providersResponse = await this.passportAttendeeSSODAO.getPassportIdentityProviders(params);
            mobx.runInAction(() => {
                this.accountIdpProviders = providersResponse;
                this.combineAndSortProviders();
            });
        } catch (error) {
            handleApiException(error);
        }
    }

    public get notificationMessage(): MessageDescriptor {
        return OE_PASSPORT_ATTENDEE_SSO_DATA_UPDATED_TEXT as MessageDescriptor;
    }

    @mobx.action
    public getIdentityProviderEnabledValidationError(): string | null {
        const hasEnabled = this.allProviders.some(p => p.enabled);
        return hasEnabled ? null : PROVIDER_VALIDATION_TEXT.defaultMessage;
    }

    public hasInvalidDomains = (): boolean => {
        return this.entity.enabled && Object.keys(this.domainValidationErrors).length > 0;
    };

    private getDomainsLength = (domains: string[] = []): number => {
        return domains.reduce((total, domain) => total + domain.length, 0);
    };

    @mobx.action
    public validateProviderDomains(providerId: string, domains: string): void {
        const updatedDomains = domains.split(',').map(d => d.trim()).filter(Boolean);
        const providerIndex = this.allProviders.findIndex(p => p.fusionAuthIdpId === providerId);
        if (providerIndex !== -1) {
            this.allProviders[providerIndex] = {
                ...this.allProviders[providerIndex],
                managedDomains: updatedDomains,
            };
        }

        const result = domainListValidator(domains);
        if (this.getDomainsLength(updatedDomains) > LONG_PLAIN_TEXT_LENGTH) {
            this.domainValidationErrors[providerId] = this.intl.formatMessage(MANAGED_DOMAINS_TOO_LONG_ERROR_TEXT);
        } else if (!result.valid && result.validationMessage) {
            this.domainValidationErrors[providerId] = result.validationMessage.defaultMessage;
        } else {
            delete this.domainValidationErrors[providerId];
        }
    }

    @mobx.action
    public handleProviderDomainsChange(providerId: string, value: string): void {
        this.tempInputValue[providerId] = value;
        if (this.hasInvalidDomains()) {
            this.validateProviderDomains(providerId, value);
        }
    }

    @mobx.action
    public handleProviderToggleChange = (providerId: string): void => {
        const providerIndex = this.allProviders.findIndex(provider => provider.fusionAuthIdpId === providerId);
        if (providerIndex !== -1) {
            this.allProviders[providerIndex] = {
                ...this.allProviders[providerIndex],
                enabled: !this.allProviders[providerIndex].enabled,
            };
        }
    };

    @mobx.action
    public handleProviderEnforcedOnAllEventsToggleChange = (providerId: string): void => {
        const providerIndex = this.allProviders.findIndex(provider => provider.fusionAuthIdpId === providerId);
        if (providerIndex !== -1) {
            this.allProviders[providerIndex] = {
                ...this.allProviders[providerIndex],
                enabledForAllEvents: !this.allProviders[providerIndex].enabledForAllEvents,
            };
        }
    };

    public hasProviderValidationErrors = (): void => {
        if (!this.entity.enabled) return;

        this.domainValidationErrors = {};

        for (const provider of this.allProviders) {
            if (!provider.enabled) continue;

            const domains = provider.managedDomains?.join(',') || '';
            const result = domainListValidator(domains);

            if (this.getDomainsLength(provider.managedDomains) > LONG_PLAIN_TEXT_LENGTH) {
                this.domainValidationErrors[provider.fusionAuthIdpId] = this.intl.formatMessage(MANAGED_DOMAINS_TOO_LONG_ERROR_TEXT);
            } else if (!result.valid && result.validationMessage) {
                this.domainValidationErrors[provider.fusionAuthIdpId] = result.validationMessage.defaultMessage;
            }
        }
    };

    public normalizeDomains = (domains?: string[]): string[] => {
        return (domains ?? []).map(d => d.trim().toLowerCase()).sort();
    };

    public isDisabled = (): boolean => {
        return (!!this.getIdentityProviderEnabledValidationError() && this.formSubmitted && this.entity.enabled)
            || this.hasInvalidDomains()
            || this.saving;
    };

    private async updateAccountSettings(): Promise<void> {
        try {
            await this.accountAdminDAO.updateOEPassportAttendeeSSOData({
                ...this.entity.getCommonAPIPayload(),
                passportAttendeeSSOEnabled: this.entity.enabled
            });
        } catch (error) {
            handleApiException(error);
        }
    }

    private async updateSelectedProviders(): Promise<void> {
        const existingProviders = this.allProviders.filter(p => p.isExisting);
        const providersToUpdate = existingProviders.filter(selected => {
            const original = this.accountIdpProviders.find(p => p.id === selected.accountIdpId);
            return original && (
                original.enabled !== selected.enabled ||
                original.enabledForAllEvents !== selected.enabledForAllEvents ||
                JSON.stringify(this.normalizeDomains(original.managedDomains)) !==
                JSON.stringify(this.normalizeDomains(selected.managedDomains))
            );
        });

        await Promise.all(providersToUpdate.map(async provider => {
            try {
                await this.passportAttendeeSSODAO.updatePassportIdentityProvider({
                    accountId: this.entity.accountId,
                    id: provider.accountIdpId,
                    fusionAuthIdpId: provider.fusionAuthIdpId,
                    fusionAuthIdpName: provider.fusionAuthIdpName,
                    enabled: provider.enabled,
                    enabledForAllEvents: provider.enabled ? provider.enabledForAllEvents : false,
                    managedDomains: provider.managedDomains,
                });
            } catch (error) {
                handleApiException(error);
            }
        }));
    }

    private async createNewProviders(): Promise<void> {
        const newProviders = this.allProviders.filter(p => !p.isExisting);
        const providersToCreate = newProviders.filter(selected => {
            const original = this.fusionProviders.find(p => p.id === selected.fusionAuthIdpId);
            const hasDomains = Array.isArray(selected.managedDomains) && selected.managedDomains.length > 0;

            return hasDomains && original && (
                original.enabled !== selected.enabled ||
                JSON.stringify(this.normalizeDomains(original.domains)) !==
                JSON.stringify(this.normalizeDomains(selected.managedDomains))
            );
        });

        if (providersToCreate.length === 0) return;

        const mappings = providersToCreate.map(p => ({
            fusionAuthIdpId: p.fusionAuthIdpId,
            fusionAuthIdpName: p.fusionAuthIdpName,
            enabled: p.enabled,
            enabledForAllEvents: p.enabled ? p.enabledForAllEvents : false,
            managedDomains: p.managedDomains,
        }));

        try {
            await this.passportAttendeeSSODAO.createBulkPassportIdentityProviders({
                accountId: this.entity.accountId,
                mappings: mappings,
            });
        } catch (error) {
            handleApiException(error);
        }
    }

    public async apiCall(): Promise<void> {
        await this.updateAccountSettings();
        if (!this.entity.enabled) return;

        await this.updateSelectedProviders();
        await this.createNewProviders();
    }

    protected shouldInterruptSubmit(): boolean {
        this.hasProviderValidationErrors();
        this.formSubmitted = true;
        return this.saving || (!!this.getIdentityProviderEnabledValidationError() && this.entity.enabled) || this.hasInvalidDomains();
    }
};

export default PassportAttendeeSSOFormVM;
